import { useState } from 'react';
import { Download, Loader2, AlertCircle } from 'lucide-react';
import { vendorApi } from '../services/api';
import { downloadBlob, fileNameFrom } from '../utils/exportCsv';

const FORMATS = {
  pdf: { label: 'PDF', extension: 'pdf', busy: 'Skapar PDF…', failed: 'PDF:en kunde inte skapas', fetch: vendorApi.getInvoicePdf },
  excel: { label: 'Excel', extension: 'xlsx', busy: 'Skapar Excel…', failed: 'Excel-filen kunde inte skapas', fetch: vendorApi.getInvoiceExcel },
} as const;

/**
 * Downloads a settlement invoice/credit memo as the printed report (PDF) or as BC's Excel export.
 * BC creates the file on request, which takes a few seconds, so the button shows progress and stays
 * disabled until the file arrives.
 */
export default function PdfButton({
  documentType,
  documentNo,
  format = 'pdf',
}: {
  documentType: string;
  documentNo: string;
  format?: keyof typeof FORMATS;
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const f = FORMATS[format];

  const download = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (state === 'busy') return;
    setState('busy');
    try {
      const response = await f.fetch(documentType, documentNo);
      downloadBlob(response.data, fileNameFrom(response.headers['content-disposition'], `${documentNo}.${f.extension}`));
      setState('idle');
    } catch {
      setState('error');
    }
  };

  return (
    <button
      type="button"
      onClick={download}
      disabled={state === 'busy'}
      aria-label={`Ladda ner ${documentNo} som ${f.label}`}
      title={state === 'error' ? `${f.failed} – försök igen` : undefined}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-sm ring-1 ring-inset transition-all duration-300 ease-fluid active:scale-[0.98] disabled:cursor-wait ${
        state === 'error' ? 'text-red-700 ring-red-200 hover:bg-red-50' : 'text-blue-700 ring-blue-200 hover:bg-blue-50'
      }`}
    >
      {state === 'busy' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : state === 'error' ? (
        <AlertCircle className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <Download className="h-3.5 w-3.5" aria-hidden />
      )}
      {state === 'busy' ? f.busy : state === 'error' ? 'Försök igen' : f.label}
      <span aria-live="polite" className="sr-only">
        {state === 'busy' ? f.busy : state === 'error' ? f.failed : ''}
      </span>
    </button>
  );
}
